const resources = [
  {
    title: "Lee County Tax Collector — DMV Services",
    description: "The Lee County Tax Collector handles Florida driver license renewals, vehicle registrations, and ID card services — documents often needed alongside passport applications. Multiple branch locations throughout Lee County including Fort Myers, Cape Coral, and Bonita Springs.",
    url: "https://www.leetc.com/",
    icon: "ri-government-line",
  },
  {
    title: "Southwest Florida International Airport (RSW)",
    description: "RSW is Estero's primary airport, located just 15–20 minutes north. Check international flight schedules, seasonal route additions, and travel advisories before booking. Growing nonstop service to Toronto, Montreal, London, and Frankfurt.",
    url: "https://www.flylcpa.com/",
    icon: "ri-plane-line",
  },
  {
    title: "U.S. State Department — Passport Information",
    description: "Official passport requirements, form downloads, and processing time estimates from the U.S. Department of State. Essential reference for first-time applicants, renewal requirements, and international travel advisories.",
    url: "https://travel.state.gov/content/travel/en/passports.html",
    icon: "ri-passport-line",
  },
  {
    title: "Koreshan State Park — Estero",
    description: "Historic state park preserving the 19th-century Koreshan Unity settlement. A unique Estero landmark with guided tours, camping, and kayaking on the Estero River. Great for visitors and residents exploring local history.",
    url: "https://www.floridastateparks.org/parks-and-trails/koreshan-state-park",
    icon: "ri-map-pin-line",
  },
  {
    title: "Florida Gulf Coast University — Study Abroad",
    description: "FGCU's international programs office coordinates study abroad opportunities, faculty research travel, and student exchange programs. Check passport and visa requirements for academic travel well before application deadlines.",
    url: "https://www.fgcu.edu/international/",
    icon: "ri-graduation-cap-line",
  },
  {
    title: "U.S. Customs and Border Protection",
    description: "Current entry requirements, customs regulations, and border crossing information for returning U.S. citizens. Essential for international travelers flying back through RSW or other Florida ports of entry.",
    url: "https://www.cbp.gov/",
    icon: "ri-shield-check-line",
  },
];

export default function EsteroHelpfulResourcesSection() {
  return (
    <section id="local-resources" className="py-16 md:py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Helpful Resources
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Estero Travel & Government Resources
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Links to local government services, travel hubs, and official reference sites that every Estero international traveler should bookmark.
          </p>
        </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Lee County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Lee County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Lee County. Online, mail, or in-person options available.", url: "https://lee.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Lee County. Required for estate and legal matters.", url: "https://lee.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Lee County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.leeclerk.org/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Lee County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.leeclerk.org/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-600 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Lee County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {resources.map((r, i) => (
            <a
              key={i}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all hover:shadow-md group block"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center mb-4 group-hover:bg-[#F6C344] transition-colors">
                <i className={`${r.icon} text-white group-hover:text-[#1A365D] transition-colors text-lg`}></i>
              </div>
              <h3 className="font-serif text-base font-bold text-[#1A365D] mb-2 group-hover:text-[#F6C344] transition-colors">
                {r.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">{r.description}</p>
              <div className="mt-4 flex items-center gap-1 text-[#F6C344] text-xs font-semibold">
                <span>Visit Site</span>
                <i className="ri-arrow-right-up-line"></i>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}